import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import { brandService, CreateBrandData, UpdateBrandData, BrandFilters } from '../services/brandService';
import { Brand } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { useAppData } from '../context/AppDataContext';
import { FiSearch, FiX } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import { formatDate } from '../utils/dateUtils';

const Brands: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { refreshBrands } = useAppData();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [formData, setFormData] = useState<CreateBrandData>({
    name: '',
    status: 'active',
    is_supplier: false,
  });
  const [filters, setFilters] = useState<BrandFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const hasLoadedRef = useRef(false);
  const filtersInitialized = useRef(false);

  // Always fetch fresh brands list on page visit (to see other users' updates)
  useEffect(() => {
    // Prevent duplicate calls from React StrictMode
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    
    const loadData = async () => {
      await loadBrands();
      // Set after load completes to prevent filters useEffect from running on initial mount
      filtersInitialized.current = true;
    };
    loadData();
  }, []);

  // Reload brands when filters change (but not on initial mount)
  useEffect(() => {
    // Only run if filters have been initialized (after initial load completes)
    if (filtersInitialized.current) {
      loadBrands();
    }
  }, [filters]);

  const loadBrands = async () => {
    // Prevent duplicate concurrent calls
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const data = await brandService.getAll(filters);
      setBrands(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters({
      ...filters,
      search: searchTerm || undefined
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await brandService.update(editingBrand.id, formData);
      } else {
        await brandService.create(formData);
      }
      setShowModal(false);
      setEditingBrand(null);
      setFormData({ name: '', status: 'active', is_supplier: false });
      // Refresh brands list and context (so other pages have latest data)
      await Promise.all([loadBrands(), refreshBrands()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save brand');
    }
  };

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      status: brand.status,
      is_supplier: !!brand.is_supplier,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) {
      return;
    }
    try {
      await brandService.delete(id);
      // Refresh brands list and context (so other pages have latest data)
      await Promise.all([loadBrands(), refreshBrands()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete brand');
    }
  };

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Brand Management</h1>
          <button
            onClick={() => {
              setEditingBrand(null);
              setFormData({ name: '', status: 'active', is_supplier: false });
              setShowModal(true);
            }}
            className="bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm sm:text-base font-medium w-full sm:w-auto font-bold"
          >
            Add Brand
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 items-end">
            <div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search brand name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <Icon component={FiSearch} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as any || undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <select
                value={
                  filters.is_supplier === undefined
                    ? ''
                    : filters.is_supplier
                    ? 'true'
                    : 'false'
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters({
                    ...filters,
                    is_supplier:
                      value === ''
                        ? undefined
                        : value === 'true',
                  });
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Suppliers</option>
                <option value="true">Supplier: Yes</option>
                <option value="false">Supplier: No</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="flex-1 bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm font-medium flex items-center justify-center gap-2 font-bold"
              >
                <Icon component={FiSearch} className="w-4 h-4" />
                <span>Search</span>
              </button>
              {(filters.search || filters.status || filters.is_supplier !== undefined) && (
                <button
                  onClick={handleClearFilters}
                  className="bg-gray-200 text-gray-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-gray-300 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Icon component={FiX} className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-6 text-sm text-gray-600">Loading...</div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Created
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {brands.map((brand) => (
                    <tr key={brand.id} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                        {brand.name}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                        <span className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-semibold rounded-full ${
                          brand.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {brand.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                        <span className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-semibold rounded-full ${
                          brand.is_supplier ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {brand.is_supplier ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                        {brand.created_at ? formatDate(brand.created_at) : '-'}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm font-medium">
                        {hasPermission('brands', 'edit') && (
                          <button
                            onClick={() => handleEdit(brand)}
                            className="text-yellow-600 hover:text-yellow-700 mr-2 sm:mr-4"
                          >
                            Edit
                          </button>
                        )}
                        {hasPermission('brands', 'delete') && (
                          <button
                            onClick={() => handleDelete(brand.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {brands.length === 0 && !loading && (
              <div className="text-center py-8 text-sm text-gray-500">No brands found</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  {editingBrand ? 'Edit Brand' : 'Add Brand'}
                </h3>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingBrand(null); }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="is_supplier"
                        checked={!!formData.is_supplier}
                        onChange={() => setFormData({ ...formData, is_supplier: true })}
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="is_supplier"
                        checked={!formData.is_supplier}
                        onChange={() => setFormData({ ...formData, is_supplier: false })}
                      />
                      No
                    </label>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingBrand(null);
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    {editingBrand ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Brands;

